import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { canAccessPremiumResources, getTierDisplayName } from '../utils/tierPermissions'
import TierUpgradePrompt from '../components/TierUpgradePrompt'
import ContactSupportModal from '../components/ContactSupportModal'
import { 
  DocumentTextIcon, 
  CodeBracketIcon,
  RocketLaunchIcon,
  ChartBarIcon,
  CloudArrowDownIcon,
  CheckCircleIcon,
  CubeIcon,
  CircleStackIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline'

export default function PremiumTemplates() {
  const { user } = useAuthStore()
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [showContactModal, setShowContactModal] = useState(false)
  
  const hasAccess = canAccessPremiumResources(user?.enrolled_tier) || user?.is_admin || user?.role === 'instructor'

  useEffect(() => {
    // Load premium templates
    setTemplates([
      {
        id: 'agent-architecture',
        title: 'Agent Architecture Template',
        description: 'Production-ready framework for building scalable AI agents with tool integration, memory management, and error handling.',
        icon: RocketLaunchIcon,
        category: 'Architecture',
        features: [
          'Multi-agent orchestration patterns',
          'Tool calling & API integration',
          'Context & memory management',
          'Error handling & recovery',
          'Production deployment guide'
        ],
        fileSize: '45 KB',
        downloads: 1247,
        lastUpdated: '2024-11-15'
      },
      {
        id: 'evaluation-suite',
        title: 'AI Evaluation Suite',
        description: 'Comprehensive testing framework for evaluating AI model performance, accuracy, and reliability in production.',
        icon: ChartBarIcon,
        category: 'Testing',
        features: [
          'Automated evaluation metrics',
          'Regression testing framework',
          'Performance benchmarking',
          'A/B testing setup',
          'Quality assurance workflows'
        ],
        fileSize: '38 KB',
        downloads: 892,
        lastUpdated: '2024-11-18'
      },
      {
        id: 'deployment-config',
        title: 'Deployment Configuration',
        description: 'Docker, Kubernetes, and cloud deployment templates for production AI applications.',
        icon: CloudArrowDownIcon,
        category: 'DevOps',
        features: [
          'Docker multi-stage builds',
          'Kubernetes manifests',
          'CI/CD pipeline templates',
          'Auto-scaling configuration',
          'Monitoring & logging setup'
        ],
        fileSize: '52 KB',
        downloads: 634,
        lastUpdated: '2024-11-21'
      },
      {
        id: 'monitoring-setup',
        title: 'Monitoring & Observability',
        description: 'Complete observability stack with metrics, logging, and alerting for AI systems.',
        icon: CheckCircleIcon,
        category: 'Operations',
        features: [
          'Prometheus metrics setup',
          'Grafana dashboards',
          'Log aggregation patterns',
          'Alert rule templates',
          'SLO/SLI definitions'
        ],
        fileSize: '41 KB',
        downloads: 521,
        lastUpdated: '2024-11-21'
      },
      {
        id: 'api-design',
        title: 'API Design & Architecture',
        description: 'Production REST/GraphQL API patterns with authentication, rate limiting, and streaming support.',
        icon: CodeBracketIcon,
        category: 'API',
        features: [
          'FastAPI implementation examples',
          'GraphQL with Strawberry',
          'JWT authentication & API keys',
          'Tier-based rate limiting',
          'Server-sent events streaming'
        ],
        fileSize: '58 KB',
        downloads: 783,
        lastUpdated: '2024-11-21'
      },
      {
        id: 'data-pipeline',
        title: 'Data Pipeline Architecture',
        description: 'ETL patterns, validation, and preprocessing workflows for AI training data.',
        icon: CircleStackIcon,
        category: 'Data Engineering',
        features: [
          'Multi-source data ingestion',
          'Schema validation with Pydantic',
          'Text preprocessing pipelines',
          'Quality monitoring metrics',
          'Airflow & Kafka examples'
        ],
        fileSize: '47 KB',
        downloads: 612,
        lastUpdated: '2024-11-21'
      },
      {
        id: 'security-compliance',
        title: 'Security & Compliance',
        description: 'Security best practices, GDPR/SOC2 compliance, and prompt injection prevention.',
        icon: ShieldCheckIcon,
        category: 'Security',
        features: [
          'Prompt injection detection',
          'PII detection & redaction',
          'GDPR compliance tools',
          'SOC 2 audit logging',
          'Incident response procedures'
        ],
        fileSize: '44 KB',
        downloads: 489,
        lastUpdated: '2024-11-21'
      },
      {
        id: 'cost-optimization',
        title: 'Cost Optimization',
        description: 'Token tracking, caching strategies, and model selection for cost efficiency.',
        icon: CurrencyDollarIcon,
        category: 'Operations',
        features: [
          'Token usage tracking',
          'Semantic & exact-match caching',
          'Model routing strategies',
          'Budget management & alerts',
          'Batch processing optimization'
        ],
        fileSize: '42 KB',
        downloads: 891,
        lastUpdated: '2024-11-21'
      },
      {
        id: 'rag-implementation',
        title: 'RAG Implementation',
        description: 'Complete retrieval-augmented generation with vector databases and optimization.',
        icon: MagnifyingGlassIcon,
        category: 'AI/ML',
        features: [
          'Vector DB setup (Qdrant/Pinecone)',
          'Advanced chunking strategies',
          'Hybrid search & reranking',
          'Context window management',
          'RAG evaluation metrics'
        ],
        fileSize: '51 KB',
        downloads: 1124,
        lastUpdated: '2024-11-21'
      },
      {
        id: 'finetuning-guide',
        title: 'Fine-tuning Guide',
        description: 'Dataset preparation, training pipelines, and deployment for fine-tuned models.',
        icon: AcademicCapIcon,
        category: 'AI/ML',
        features: [
          'Dataset preparation & validation',
          'OpenAI fine-tuning workflow',
          'HuggingFace LoRA training',
          'Hyperparameter optimization',
          'ROI & cost analysis'
        ],
        fileSize: '49 KB',
        downloads: 967,
        lastUpdated: '2024-11-21'
      }
    ])
    setLoading(false)
  }, [])

  const handleDownload = async (template) => {
    if (!hasAccess) return
    
    try {
      // Fetch the markdown file from secured API endpoint instead of static course-content
      const baseURL = import.meta.env.VITE_API_URL || '/api';
      const token = localStorage.getItem('auth-token');
      
      const response = await fetch(`${baseURL}/premium/templates/${template.id}`, {
        credentials: 'include',
        headers: { 
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
      if (!response.ok) {
        throw new Error('Template not found')
      }
      const data = await response.json()
      const content = data.content || ''

      // Create watermark header with user information
      const downloadDate = new Date().toISOString().split('T')[0]
      const watermark = `<!--
================================================================================
LICENSED TEMPLATE - CTE Course Platform
================================================================================
Licensed to: ${user.name} (${user.email})
License Tier: ${getTierDisplayName(user.enrolled_tier)}
Download Date: ${downloadDate}
Template ID: ${template.id}

TERMS OF USE:
This template is licensed exclusively to the individual named above.
Redistribution, sharing, or unauthorized use is strictly prohibited.
For support or licensing questions: techsupport@cteconsultants.net

© ${new Date().getFullYear()} CTE Consultants & General Sales LLC. All rights reserved.
================================================================================
-->

`
      
      // Prepend watermark to content
      const watermarkedContent = watermark + content
      
      // Create a blob and download link
      const blob = new Blob([watermarkedContent], { type: 'text/markdown' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${template.id}.md`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Failed to download template:', error)
      alert('Failed to download template. Please try again or contact support.')
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h1 className="mt-4 text-3xl font-bold text-gray-900">Premium Templates</h1>
          <p className="mt-2 text-lg text-gray-600">
            Advanced templates and frameworks for production AI development
          </p>
        </div>

        <TierUpgradePrompt
          variant="modal"
          requiredTier="premium"
          featureName="Premium Templates"
          featureDescription="Get access to production-ready templates, frameworks, and best practices guides."
        />

        {/* Preview cards (locked) */}
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          {templates.slice(0, 4).map((template) => (
            <div key={template.id} className="relative bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6 opacity-60">
              <div className="absolute top-4 right-4 bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
                🔒 Premium Only
              </div>
              <template.icon className="h-10 w-10 text-gray-400 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">{template.title}</h3>
              <p className="text-gray-600 mb-4">{template.description}</p>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>{template.fileSize}</span>
                <span>•</span>
                <span>{template.downloads} downloads</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-gray-900">Premium Templates</h1>
          <span className="bg-indigo-100 text-indigo-800 px-4 py-2 rounded-full text-sm font-semibold">
            {user?.role === 'instructor' ? 'Instructor' : (user?.is_admin ? 'Administrator' : getTierDisplayName(user?.enrolled_tier))} Access
          </span>
        </div>
        <p className="text-lg text-gray-600">
          Production-ready templates, frameworks, and configuration files for building enterprise AI applications.
        </p>
      </div>

      {/* Templates Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {templates.map((template) => (
          <div 
            key={template.id} 
            className="bg-white rounded-lg shadow-sm border-2 border-indigo-200 hover:border-indigo-400 hover:shadow-md p-6 transition-all"
          >
            {/* Icon & Badge */}
            <div className="flex items-start justify-between mb-4">
              <template.icon className="h-10 w-10 text-indigo-600" />
            </div>

            {/* Content */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xl font-bold text-gray-900">{template.title}</h3>
              </div>
              <p className="text-gray-600 mb-3">{template.description}</p>
              
              {/* Category Badge */}
              <span className="inline-block bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-semibold">
                {template.category}
              </span>
            </div>

            {/* Features List */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Includes:</h4>
              <ul className="space-y-1">
                {template.features.slice(0, 3).map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
                {template.features.length > 3 && (
                  <li className="text-sm text-gray-500 italic">
                    +{template.features.length - 3} more...
                  </li>
                )}
              </ul>
            </div>

            {/* Meta Info */}
            <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
              <span>{template.fileSize}</span>
              <span>{template.downloads.toLocaleString()} downloads</span>
            </div>

            {/* Download Button */}
            <button
              onClick={() => handleDownload(template)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-colors bg-indigo-600 text-white hover:bg-indigo-700"
            >
              <CloudArrowDownIcon className="h-5 w-5" />
              Download Template
            </button>
          </div>
        ))}
      </div>

      {/* Help Section */}
      <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Need Help?</h3>
        <p className="text-blue-800 mb-4">
          These templates are designed to accelerate your AI development workflow. Each includes detailed documentation and examples.
        </p>
        <button 
          onClick={() => setShowContactModal(true)}
          className="inline-flex items-center text-blue-600 hover:text-blue-800 font-semibold"
        >
          Contact Support →
        </button>
      </div>

      <ContactSupportModal 
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
    </div>
  )
}
